/**
 * Delivery Time Calculator
 * Calculates delivery times based on business rules:
 * - Orders before 22:00 on weekdays = next day delivery
 * - Orders on weekends = Monday delivery
 */

class DeliveryTimeCalculator {
  constructor() {
    this.cutoffTime = 22; // 22:00 cutoff
    this.weekendDays = [0, 6]; // Sunday = 0, Saturday = 6
  }

  /**
   * Get the next business day
   * @param {Date} startDate - Starting date
   * @returns {Date} Next business day
   */
  getNextBusinessDay(startDate) {
    const nextDay = new Date(startDate);
    nextDay.setDate(nextDay.getDate() + 1);
    
    // Skip weekends
    while (this.weekendDays.includes(nextDay.getDay())) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    
    return nextDay;
  }

  /**
   * Get the next Monday
   * @param {Date} startDate - Starting date
   * @returns {Date} Next Monday
   */
  getNextMonday(startDate) {
    const nextMonday = new Date(startDate);
    const daysUntilMonday = (8 - nextMonday.getDay()) % 7;
    nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
    return nextMonday;
  }

  /**
   * Get Dutch day name
   * @param {Date} date - Date to get day name for
   * @returns {string} Dutch day name
   */
  getDayName(date) {
    const dayNames = [
      'zondag',
      'maandag', 
      'dinsdag',
      'woensdag',
      'donderdag',
      'vrijdag',
      'zaterdag'
    ];
    return dayNames[date.getDay()];
  }

  /**
   * Calculate delivery date based on current time
   * @returns {Object} Object with delivery date and message
   */
  calculateDeliveryDate() {
    const now = new Date();
    const currentHour = now.getHours();
    const currentDay = now.getDay();
    const isWeekend = this.weekendDays.includes(currentDay);
    
    let deliveryDate;
    let message;
    
    if (isWeekend) {
      // Weekend logic
      if (currentDay === 0) { // Sunday
        if (currentHour < this.cutoffTime) {
          // Sunday before 22:00: Monday delivery
          deliveryDate = this.getNextMonday(now);
          message = "Vandaag besteld = maandag in huis";
        } else {
          // Sunday after 22:00: Tuesday delivery
          const nextDay = new Date(now);
          nextDay.setDate(nextDay.getDate() + 1);
          deliveryDate = this.getNextBusinessDay(nextDay);
          const deliveryDayName = this.getDayName(deliveryDate);
          message = `Nu besteld = ${deliveryDayName} in huis`;
        }
      } else { // Saturday
        // Saturday: always Monday delivery
        deliveryDate = this.getNextMonday(now);
        message = "Vandaag besteld = maandag in huis";
      }
    } else {
      // Weekday
      if (currentHour < this.cutoffTime) {
        // Before 22:00: next business day
        deliveryDate = this.getNextBusinessDay(now);
        
        // Check if it's early morning (before 6:00) and adjust message accordingly
        if (currentHour < 6) {
          const deliveryDayName = this.getDayName(deliveryDate);
          message = `Nu besteld = ${deliveryDayName} in huis`;
        } else {
          message = "Voor 22:00 besteld = morgen in huis";
        }
      } else {
        // After 22:00: day after next business day
        const nextDay = new Date(now);
        nextDay.setDate(nextDay.getDate() + 1);
        deliveryDate = this.getNextBusinessDay(nextDay);
        
        // Get the specific day name for the delivery date
        const deliveryDayName = this.getDayName(deliveryDate);
        message = `Nu besteld = ${deliveryDayName} in huis`;
      }
    }
    
    return {
      deliveryDate,
      message,
      isWeekend,
      isBeforeCutoff: currentHour < this.cutoffTime
    };
  }

  /**
   * Format date to Dutch format
   * @param {Date} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDate(date) {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('nl-NL', options);
  }

  /**
   * Update all delivery time elements on the page
   */
  updateDeliveryTimes() {
    const result = this.calculateDeliveryDate();
    
    // Update elements with delivery time information
    const deliveryElements = document.querySelectorAll('[data-delivery-time]');
    
    deliveryElements.forEach(element => {
      const type = element.getAttribute('data-delivery-time');
      
      switch (type) {
        case 'message':
          // Preserve existing HTML structure but update the text content
          const currentHTML = element.innerHTML;
          let newHTML = result.message;
          
          // Preserve highlight spans if they exist
          if (currentHTML.includes('<span class="highlight">')) {
            newHTML = newHTML.replace('22:00', '<span class="highlight">22:00</span>');
            newHTML = newHTML.replace('morgen', '<span class="highlight">morgen</span>');
            newHTML = newHTML.replace('maandag', '<span class="highlight">maandag</span>');
            newHTML = newHTML.replace('dinsdag', '<span class="highlight">dinsdag</span>');
            newHTML = newHTML.replace('woensdag', '<span class="highlight">woensdag</span>');
            newHTML = newHTML.replace('donderdag', '<span class="highlight">donderdag</span>');
            newHTML = newHTML.replace('vrijdag', '<span class="highlight">vrijdag</span>');
            newHTML = newHTML.replace('zaterdag', '<span class="highlight">zaterdag</span>');
            newHTML = newHTML.replace('zondag', '<span class="highlight">zondag</span>');
          }
          
          element.innerHTML = newHTML;
          break;
        case 'date':
          element.textContent = this.formatDate(result.deliveryDate);
          break;
        case 'full':
          element.innerHTML = `<strong>${result.message}</strong>`;
          break;
      }
    });
  }

  /**
   * Initialize the calculator and update times
   */
  init() {
    if (!document.querySelector('[data-delivery-time]')) return;

    this.updateDeliveryTimes();

    setInterval(() => {
      this.updateDeliveryTimes();
    }, 60000);
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const calculator = new DeliveryTimeCalculator();
  calculator.init();
  
  // Make available globally for debugging
  window.deliveryCalculator = calculator;
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DeliveryTimeCalculator;
} 